import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';
import { maintenanceApi } from '@/api/maintenance';
import { clientsApi } from '@/api/clients';
import { cctvApi } from '@/api/cctv';
import { maintenanceSchema, type MaintenanceFormData } from '@/utils/validators';
import type { Client, CCTV } from '@/types';
import FormField from '@/components/FormField';
import LoadingSpinner from '@/components/LoadingSpinner';

export function MaintenanceForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editId = id && id !== 'new' ? id : null;
  const isEditing = !!editId;

  const [selectedClientId, setSelectedClientId] = useState('');

  const { data: maintResponse, isLoading: loadingMaint } = useQuery({
    queryKey: ['maintenance', editId],
    queryFn: () => maintenanceApi.getById(editId!),
    enabled: isEditing,
  });

  const { data: clientsResponse } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.getAll({ limit: 100 }),
  });

  const { data: cctvResponse } = useQuery({
    queryKey: ['cctv', 'byClient', selectedClientId],
    queryFn: () => cctvApi.getAll({ clientId: selectedClientId, limit: 100 }),
    enabled: !!selectedClientId,
  });

  const clients: Client[] = clientsResponse?.data?.data || [];
  const cctvList: CCTV[] = cctvResponse?.data?.data || [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MaintenanceFormData>({
    resolver: zodResolver(maintenanceSchema),
    defaultValues: {
      clientId: '',
      cctvId: '',
      type: 'preventive',
      description: '',
      status: 'pending',
      priority: 'normal',
      scheduledDate: '',
      technician: '',
      cost: 0,
      notes: '',
    },
  });

  const watchedClientId = watch('clientId');

  useEffect(() => {
    if (watchedClientId) {
      setSelectedClientId(watchedClientId);
    }
  }, [watchedClientId]);

  useEffect(() => {
    if (isEditing && maintResponse?.data?.data) {
      const maint = maintResponse.data.data as any;
      reset({
        clientId: maint.clientId || '',
        cctvId: maint.cctvId || '',
        type: maint.type || 'preventive',
        description: maint.description || '',
        status: maint.status || 'pending',
        priority: maint.priority || 'normal',
        scheduledDate: maint.scheduledDate ? maint.scheduledDate.split('T')[0] : '',
        technician: maint.technician || maint.assignedTo || '',
        cost: maint.cost ?? 0,
        notes: maint.notes || '',
      });
      if (maint.clientId) {
        setSelectedClientId(maint.clientId);
      }
    }
  }, [isEditing, maintResponse, reset]);

  const createMutation = useMutation({
    mutationFn: (data: MaintenanceFormData) => maintenanceApi.create(data as any),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      toast.success('Mantenimiento creado exitosamente');
      navigate(`/maintenance/${res.data.data.id}`);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al crear el mantenimiento');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: MaintenanceFormData) => maintenanceApi.update(editId!, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', editId] });
      toast.success('Mantenimiento actualizado exitosamente');
      navigate(`/maintenance/${editId}`);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al actualizar el mantenimiento');
    },
  });

  const onSubmit = async (data: MaintenanceFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && loadingMaint) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-surface-900">
          {isEditing ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="rounded-lg border border-surface-200 bg-white p-6 shadow-sm space-y-5">
          <h2 className="text-lg font-semibold text-surface-900">Información General</h2>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField label="Cliente" error={errors.clientId?.message} required>
              <select
                {...register('clientId')}
                onChange={(e) => {
                  register('clientId').onChange(e);
                  setSelectedClientId(e.target.value);
                  setValue('cctvId', '');
                }}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Seleccionar cliente...</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} {c.company ? `- ${c.company}` : ''}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Equipo CCTV" error={errors.cctvId?.message}>
              <select
                {...register('cctvId')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                disabled={!selectedClientId}
              >
                <option value="">{selectedClientId ? 'Seleccionar equipo...' : 'Primero selecciona un cliente'}</option>
                {cctvList.map((cctv) => (
                  <option key={cctv.id} value={cctv.id}>
                    {cctv.brand} {cctv.model} - {cctv.location || 'Sin ubicación'}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Tipo" error={errors.type?.message} required>
              <select
                {...register('type')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="preventive">Preventivo</option>
                <option value="corrective">Correctivo</option>
              </select>
            </FormField>

            <FormField label="Prioridad" error={errors.priority?.message}>
              <select
                {...register('priority')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="critical">Crítica</option>
              </select>
            </FormField>

            {isEditing && (
              <FormField label="Estado" error={errors.status?.message}>
                <select
                  {...register('status')}
                  className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="pending">Pendiente</option>
                  <option value="in-progress">En Progreso</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </FormField>
            )}

            <FormField label="Fecha Programada" error={errors.scheduledDate?.message}>
              <input
                type="date"
                {...register('scheduledDate')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </FormField>

            <FormField label="Técnico Asignado" error={errors.technician?.message}>
              <input
                type="text"
                {...register('technician')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Nombre del técnico"
              />
            </FormField>

            <FormField label="Costo" error={errors.cost?.message} required>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('cost', { valueAsNumber: true })}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="0.00"
              />
            </FormField>
          </div>

          <FormField label="Descripción" error={errors.description?.message} required>
            <textarea
              {...register('description')}
              rows={4}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Describe el trabajo a realizar (mínimo 10 caracteres)..."
            />
          </FormField>

          <FormField label="Notas Adicionales" error={errors.notes?.message}>
            <textarea
              {...register('notes')}
              rows={3}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Notas adicionales..."
            />
          </FormField>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-surface-300 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {isEditing ? 'Actualizando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEditing ? 'Actualizar' : 'Guardar'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default MaintenanceForm;
